<template>
  <div class="rest-info__section">
    <content-container>
      <div class="rest-info__title-wrapper">
        <i v-if="!isPartner" class="fas fa-info-circle non-partner-info" v-tooltip="nonPartnerMessage"></i>
        <h2 class="rest-info__title">
          {{ name }}
        </h2>
      </div>
      <div class="rest-info__more-info-wrapper">
        <a class="rest-info__info rest-info__back-link rest-info__flex" :href="'/' + cityName + '/restaurants/'">
          <icon-base width="16px" height="14px" class="rest-info__back-arrow-icon" :iconName="'Back to All Restaurants'">
            <icon-arrow-right />
          </icon-base>
          <span>Back to All Restaurants</span>
        </a>
        <v-popover
          :open="showDelTimePopover"
          :popoverInnerClass="'popover-inner'"
          :popoverBaseClass="['popover', 'tooltip', 'del-time-base']"
        >
          <div v-if="deliveryTime.type === 'asap' && waitTime" class="rest-info__info rest-info__line rest-info__flex">
            <button @click.prevent="toggleDelTimePopover" class="del-time-popover-btn" aria-label="Update Delivery Time">
              <flying-fork-dark class="rest-info__flyingfork-icon" />
              Estimate: {{ waitTime }} minutes
            </button>
          </div>
          <div v-if="deliveryTime.type === 'later' && deliveryTime.time" class="rest-info__info rest-info__line rest-info__flex">
            <button @click.prevent="toggleDelTimePopover" class="del-time-popover-btn" aria-label="Update Delivery Time">
              <icon-base width="20px" height="20px" class="rest-info__clock-icon" :iconName="'clock'">
                <icon-clock />
              </icon-base>
              {{ deliveryTime.time }}
            </button>
          </div>
          <template slot="popover">
            <div class="del-time-prompt">When would you like it delivered?</div>
            <div class="del-time-buttons-wrapper">
              <button
                @click="handleDeliveryASAP"
                :class="['del-time-btn', { delTimeSelected: deliveryTime.type !== 'later' }]"
                aria-label="Set delivery as soon as possible."
              >
                <flying-fork-dark class="rest-info__flyingfork-icon" />ASAP
              </button>
              <button
                @click="handleDelTimeUpdate"
                :class="['del-time-btn', { delTimeSelected: deliveryTime.type === 'later' }]"
                aria-label="Update Delivery Time"
              >
                <icon-base width="20px" height="20px" class="rest-info__clock-icon" :iconName="'clock'">
                  <icon-clock />
                </icon-base>
                Schedule Delivery
              </button>
            </div>
          </template>
        </v-popover>
        <button class="rest-info__more-button rest-info__line" @click="handleInfoOpen()">More Info +</button>
        <div v-if="tags" class="rest-info__line rest-info__info">
          <span v-for="(tag, index) in tags" :key="tag" class="rest-info__tag">
            {{ tag }}<span v-if="index < tags.length - 1">,</span>
          </span>
        </div>
        <div v-if="deliveryCost" class="rest-info__info rest-info__line rest-info__flex">
          ${{ deliveryCost }} Delivery
          <span v-if="distance" class="rest-info__distance">{{ distance }} miles away</span>
        </div>
        <a
          v-if="showMenuEditLink"
          class="rest-info__info rest-info__line rest-info__back-link rest-info__flex edit-menu-link"
          :href="'/restMenuEdit.pl?restId=' + restId"
        >
          <i class="far fa-edit"></i>
          <span>Edit Menu</span>
        </a>
      </div>
    </content-container>
  </div>
</template>

<script>
import ContentContainer from './ContentContainer.vue'
import IconBase from './IconBase.vue'
import IconClock from './icons/IconClock.vue'
import IconArrowRight from './icons/IconArrowRight.vue'
import FlyingForkDark from './icons/FlyingForkDark.vue'

export default {
  name: 'RestInfoBanner',
  components: {
    ContentContainer,
    IconBase,
    IconClock,
    IconArrowRight,
    FlyingForkDark,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    tags: {
      type: Array,
      default: () => [],
    },
    deliveryCost: [String, Number],
    distance: [String, Number],
    waitTime: [String, Number],
    deliveryTime: {
      type: Object,
      default: () => {},
    },
    restColor: {
      type: String,
      default: '#0184c6',
    },
    cityName: String,
    showMenuEditLink: {
      type: Boolean,
      default: false,
    },
    restId: {
      type: [String, Number],
    },
    isPartner: {
      type: Boolean,
      default: true,
    },
    nonPartnerMessage: {
      type: String,
    },
  },
  data: function () {
    return { isActive: false, showDelTimePopover: false }
  },
  methods: {
    handleInfoOpen() {
      this.$emit('showInfo')
    },
    handleDelTimeUpdate() {
      this.showDelTimePopover = false
      this.$emit('showDelTime')
    },
    handleDeliveryASAP() {
      this.showDelTimePopover = false
      if (this.deliveryTime.type === 'asap') {
        return false
      }
      this.$emit('deliveryASAP')
    },
    toggleDelTimePopover() {
      this.showDelTimePopover = !this.showDelTimePopover
    },
  },
}
</script>

<style scoped lang="scss">
.rest-info__section {
  font-weight: 700;
  font-size: 14px;
  background-color: $white;
  color: $black;
  padding-top: 15px;
}

.rest-info__title-wrapper {
  margin-bottom: 10px;
  text-align: center;
  @media (min-width: $tabletPortrait) {
    display: flex;
    align-items: center;
    text-align: left;
  }
}

.rest-info__like-icon {
  margin: 10px 10px 0;
  color: $white;
}

.rest-info__like-icon.active {
  color: $tomato;
}

.rest-info__title {
  font-weight: 900;
  font-size: 30px;
  line-height: 1.25;
}

.non-partner-info {
  font-size: 16px;
  cursor: help;
  margin-right: 5px;
}

.rest-info__more-info-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  flex-direction: column;
  gap: 5px;

  @media (min-width: $tabletPortrait) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0;
  }
}

.rest-info__more-button {
  font-weight: 900;
  text-transform: uppercase;
  font-size: inherit;
  background-color: $white;
  border: none;
  margin-right: 0;

  &:hover,
  &:focus {
    color: $tomato;
  }

  @media (min-width: $tabletPortrait) {
    margin-right: 15px;
  }
}

.rest-info__info {
  padding-right: 15px;
  text-align: center;
}

.rest-info__line {
  border-left: none;
  padding-left: 15px;
  text-align: center;

  @media (min-width: $tabletPortrait) {
    border-left: 1px solid $gray_6;
  }
}

.rest-info__flex {
  display: flex;
  align-items: center;
}

.rest-info__distance {
  font-weight: 300;
  padding-left: 5px;
}

.rest-info__back-link {
  color: $white;
  -webkit-transition: transform 0.3s ease;
  transition: transform 0.3s ease;
  text-align: center;
  padding: 5px;
  margin-right: 15px;
  background-color: $royal;
  border-radius: 22.5px;
  box-shadow: 1px 1px 5px 0 rgba(171, 168, 168, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border: none;

  &:hover {
    color: $white;
    cursor: pointer;
    transform: translate(0, -2px);
    text-decoration: none;

    .rest-info__back-arrow-icon {
      text-decoration: none;
    }
  }
  @media (max-width: $tabletPortrait) {
    margin-right: 0px;
  }
}

.rest-info__clock-icon,
.rest-info__flyingfork-icon {
  padding-right: 5px;
}

.rest-info__back-arrow-icon {
  transform: rotate(-180deg);
  margin-right: 5px;
}

.fa-edit {
  margin-right: 5px;
}

.del-time-buttons-wrapper {
  display: flex;
  flex-flow: row;
  justify-content: space-around;
  width: 100%;
}
.del-time-prompt {
  display: flex;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
}

.del-time-popover-btn,
.del-time-btn {
  cursor: pointer;
  -webkit-transition: transform 0.3s ease;
  transition: transform 0.3s ease;
  text-align: center;
  padding: 5px;
  background-color: $gold;
  border-radius: 22.5px;
  box-shadow: 1px 1px 5px 0 rgba(171, 168, 168, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-weight: 700;

  &:hover {
    transform: translate(0, -2px);
  }
}

.del-time-popover-btn {
  border: 1px solid;
}

.del-time-btn {
  background-color: #eee;
  border: 1px solid transparent;
}

.delTimeSelected {
  background-color: $gold;
  border: 3px solid $royal;
}
</style>
